import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {
  Alert, Box, Chip, IconButton, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Tooltip, Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { api } from "../api/client";
import type { ProjectReport, ReportRow } from "../api/types";
import { BackButton } from "../components/BackButton";
import { formatMoney } from "../lib/money";

const KOLUMNY = [
  { key: "budget", label: "Plan" },
  { key: "committed", label: "Zakontraktowane" },
  { key: "open_commitment", label: "Otwarte" },
  { key: "actual", label: "Rzeczywiste" },
  { key: "paid", label: "Zapłacone" },
  { key: "remaining", label: "Wolne" },
] as const;

export function ReportPage() {
  const { code = "" } = useParams();
  const [zwiniete, setZwiniete] = useState<Set<string>>(new Set());

  const { data } = useQuery({
    queryKey: ["report", code],
    queryFn: () => api.get<ProjectReport>(`/reports/four-columns/${code}`),
  });

  function przelacz(id: string) {
    setZwiniete((s) => {
      const kopia = new Set(s);
      if (kopia.has(id)) kopia.delete(id);
      else kopia.add(id);
      return kopia;
    });
  }

  function wiersze(rows: ReportRow[]): React.ReactNode[] {
    return rows.flatMap((row) => {
      const maDzieci = row.children.length > 0;
      const rozwiniety = !zwiniete.has(row.account_code_id);
      const element = (
        // Klikalna jest **cała belka**, nie sama strzałka: trafienie w ikonę
        // o boku dwudziestu pikseli jest wymaganiem precyzji, a nie decyzją.
        // Strzałka zostaje jako wskazówka, że wiersz się rozwija.
        <TableRow key={row.account_code_id} hover
                  onClick={maDzieci ? () => przelacz(row.account_code_id) : undefined}
                  sx={{ bgcolor: row.depth === 0 ? "action.hover" : undefined,
                        cursor: maDzieci ? "pointer" : undefined }}>
          <TableCell sx={{ pl: 1 + row.depth * 2.5 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {maDzieci ? (
                <IconButton
                  size="small"
                  aria-label={rozwiniety ? "Zwiń pozycje" : "Rozwiń pozycje"}
                  onClick={(e) => {
                    // Bez tego kliknięcie w strzałkę przełącza dwa razy:
                    // raz tutaj, raz na wierszu, do którego dochodzi.
                    e.stopPropagation();
                    przelacz(row.account_code_id);
                  }}
                >
                  {rozwiniety ? <KeyboardArrowDownIcon fontSize="small" />
                              : <KeyboardArrowRightIcon fontSize="small" />}
                </IconButton>
              ) : <Box sx={{ width: 30 }} />}
              <Typography variant="body2" fontWeight={row.depth === 0 ? 600 : 400}>
                {row.code} {row.name}
              </Typography>
            </Stack>
          </TableCell>
          {KOLUMNY.map((k) => {
            const wartosc = row[k.key];
            const przekroczone = k.key === "remaining" && row.is_overrun;
            return (
              <TableCell key={k.key} align="right"
                         sx={{ color: przekroczone ? "error.main" : undefined,
                               fontWeight: przekroczone ? 600 : undefined,
                               fontVariantNumeric: "tabular-nums" }}>
                {formatMoney(wartosc)}
              </TableCell>
            );
          })}
        </TableRow>
      );
      return maDzieci && rozwiniety ? [element, ...wiersze(row.children)] : [element];
    });
  }

  if (!data) return null;

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="baseline" spacing={2}>
        <BackButton fallback={`/projekty/${code}`} />
        <Typography variant="h6">
          {data.project.code} — {data.project.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {data.project.organization.name}
        </Typography>
        {data.budget_version && (
          <Chip size="small"
                color={data.budget_version.authoritative ? "success" : "warning"}
                label={`budżet v${data.budget_version.number}${
                  data.budget_version.authoritative ? "" : " (niezatwierdzony)"}`} />
        )}
      </Stack>

      {data.warnings.map((w) => (
        <Alert key={w} severity="warning">{w}</Alert>
      ))}

      <Paper variant="outlined" sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell>Pozycja</TableCell>
              {KOLUMNY.map((k) => (
                <TableCell key={k.key} align="right">
                  {k.key === "paid" && !data.paid_source_connected ? (
                    <Tooltip title="Źródło (InsERT) niepodłączone — etap 3.">
                      <span style={{ opacity: 0.5 }}>{k.label}</span>
                    </Tooltip>
                  ) : k.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {wiersze(data.rows)}
            <TableRow sx={{ "& td": { borderTop: 2, fontWeight: 600 } }}>
              <TableCell>Razem</TableCell>
              {KOLUMNY.map((k) => (
                <TableCell key={k.key} align="right"
                           sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {formatMoney(data.totals[k.key])}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </Paper>

      {Number(data.unallocated_actual) > 0 && (
        <Alert severity="warning">
          Koszty bez przypisania do kosztorysu: {formatMoney(data.unallocated_actual)} zł
          ({data.unallocated_count} dok.). O tyle kolumna „Rzeczywiste” jest zaniżona.
        </Alert>
      )}
    </Stack>
  );
}
